import { JsonRpcProvider, JsonRpcSigner } from '@ethersproject/providers';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { ZERO } from '@synfutures/sdks-perp';
import { BigNumber, ContractReceipt, ethers } from 'ethers';

import { OPERATION_TX_TYPE, TRADE_FORM_TYPE } from '@/constants';
import { WrappedBigNumber } from '@/entities/WrappedBigNumber';
import { TokenInfo } from '@/types/token';
import { ITokenAllowanceInfo } from '@/types/user';
import { formatEther } from '@/utils/numberUtil';
import { getERC2O } from '@/utils/signer';
import { adjustBalanceNumberToTokenDecimals, isNativeTokenAddr } from '@/utils/token';

import { QUERY_KEYS } from '@/constants/query';
import { INotificationArgs } from '@/hooks/useTxNotification';
import i18n from '@/i18n';

import { DAPP_CHAIN_CONFIGS } from '@/constants/chain';
import { queryClient } from '@/global';
import { getSynContract } from '@/utils/contract';
import { sendGelatoTransactionAndWait } from '@/utils/gelatoTransaction';
import { Context } from '@derivation-tech/context/dist/context';
import { useAgentStore } from '../agent/store';
import { ITokenBalanceFromApiResponse } from '../balance/types';
import { addTransaction } from '../transaction/actions';

export const approveToken = createAsyncThunk(
  'user/approveToken',
  async (
    {
      signer,
      provider,
      marginToken,
      chainId,
      amount,
      userAddr,
      spenderAddress,
      sdk,
      sendTxNotification,
    }: // formType = TRADE_FORM_TYPE.TradeForm,
    {
      signer: JsonRpcSigner;
      provider?: JsonRpcProvider;
      marginToken: TokenInfo;
      chainId: number;
      userAddr: string;
      formType?: TRADE_FORM_TYPE;
      amount: BigNumber;
      spenderAddress?: string;
      sdk: Context;
      sendTxNotification?: (args: INotificationArgs) => void;
    },
    { dispatch },
  ): Promise<boolean> => {
    console.log('🚀 ~ sendTxNotification:', sendTxNotification);
    try {
      const agentSettings = useAgentStore.getState().userAgentSettings[userAddr];
      const agentAddr = agentSettings?.agentAddress;
      const isEnableAgentMode = agentSettings?.isEnableAgentMode;
      console.log('🚀 ~ agentAddr:', agentAddr);

      if (!spenderAddress) {
        spenderAddress = sdk.perp.contracts.gate.address;
      }

      const balanceInfos = queryClient.getQueryData<ITokenBalanceFromApiResponse>(
        QUERY_KEYS.BALANCE.TOKEN_BALANCE(chainId, userAddr),
      );
      const balanceInfo = balanceInfos?.find((b) => b.address.toLowerCase() === marginToken.address.toLowerCase());
      const balance = WrappedBigNumber.from(balanceInfo?.balance || amount).wadValue;
      const approvalAmountWad = adjustBalanceNumberToTokenDecimals(balance, marginToken.decimals);

      // use agent mode
      if (!DAPP_CHAIN_CONFIGS[chainId]?.disable1CT && isEnableAgentMode && agentAddr) {
        const erc20PermitContract = await getSynContract({
          chainId,
          type: 'erc20Permit',
          address: marginToken.address,
          signerOrProvider: signer,
        });
        console.log('🚀 ~ erc20Permit:', erc20PermitContract);
        const currentNonce = await erc20PermitContract.nonces(userAddr);
        console.log(`Current permit nonce: ${currentNonce.toString()}`);

        // Set deadline (1 hour from now)
        const deadline = Math.floor(Date.now() / 1000) + 3600;
        console.log(`Deadline: ${deadline} (${new Date(deadline * 1000).toISOString()})`);

        // const amountWad = parseUnits(amount.toString(), marginToken.decimals);
        const approveAmount = approvalAmountWad;
        console.log(`Approving ${approveAmount.toString()} USDM to Gate contract...`);

        // Get token name for domain
        const tokenName = await erc20PermitContract.name();
        console.log(`Token name: ${tokenName}`);

        // Create EIP-712 signature for permit
        const domain = {
          name: tokenName,
          version: '1',
          chainId: chainId,
          verifyingContract: marginToken.address,
        };

        const types = {
          Permit: [
            { name: 'owner', type: 'address' },
            { name: 'spender', type: 'address' },
            { name: 'value', type: 'uint256' },
            { name: 'nonce', type: 'uint256' },
            { name: 'deadline', type: 'uint256' },
          ],
        };

        const value = {
          owner: userAddr,
          spender: spenderAddress,
          value: approveAmount,
          nonce: currentNonce,
          deadline: deadline,
        };

        const signature = await signer._signTypedData(domain, types, value);
        console.log('🚀 ~ signature:', signature);

        const sig = ethers.utils.splitSignature(signature);
        const batchData = erc20PermitContract.interface.encodeFunctionData('permit', [
          userAddr, // owner
          spenderAddress, // spender
          approveAmount, // value
          deadline, // deadline
          sig.v, // v
          sig.r, // r
          sig.s, // s
        ]);

        // Use the common gelato transaction method
        const txType = OPERATION_TX_TYPE.APPROVE;
        const result = await sendGelatoTransactionAndWait({
          chainId,
          batchData: batchData,
          targetContract: marginToken.address,
          userAddr, // Add userAddr parameter
          provider,
          hooks: {
            onStart: (taskId) => {
              const title = i18n.t(`notification.${txType}.info`);
              sendTxNotification?.({
                type: 'info',
                message: title,
                taskId,
              });
            },
            onSuccess: (taskId, transactionHash) => {
              sendTxNotification?.({
                message: `${i18n.t(`notification.${txType}.success`)}`,
                description: `${i18n.t(`notification.${txType}.success`)}`,
                taskId: taskId,
                tx: transactionHash,
                type: 'success',
              });
            },
            onError: (error, taskId) => {
              sendTxNotification?.({
                message: `${i18n.t(`notification.${txType}.error`)}`,
                description: error || `${i18n.t(`notification.${txType}.errorDesc`)}`,
                taskId,
                type: 'error',
              });
            },
          },
        });

        if (!result.success) {
          throw new Error(result.error || 'Transaction failed');
        } else {
          dispatch(
            checkTokenAllowance({
              userAddress: userAddr,
              chainId,
              marginToken,
              spenderAddress,
              sdk,
            }),
          );
        }
      } else {
        const erc20 = await getERC2O(marginToken.address, signer);

        // const {
        //   balance: { chainBalanceMap },
        // } = getState() as AppState;
        // const balanceInfo = _.get(chainBalanceMap, [chainId, userAddr, marginToken.address]);
        // const balance = WrappedBigNumber.from(balanceInfo?.balance || amount).wadValue;

        // const nonce = await getNonnce(signer, routerAddress);
        const approveResult = await erc20.approve(
          spenderAddress,
          approvalAmountWad,
          // {
          //   nonce: nonce,
          // },
        );
        dispatch(
          addTransaction({
            chainId,
            traderAddr: signer._address.toLowerCase(),
            txInfo: {
              type: OPERATION_TX_TYPE.APPROVE,
              chainId,
              from: signer._address.toLowerCase(),
              gasLimit: formatEther(approveResult?.gasLimit),
              txHash: approveResult.hash,
              transactionResponse: approveResult,
            },
          }),
        );

        // use websocket provider
        let receipt: ContractReceipt | undefined = undefined;

        if (provider) {
          receipt = await provider.waitForTransaction(approveResult.hash);
        } else {
          receipt = await approveResult.wait();
        }
        if (receipt?.status === 1) {
          dispatch(
            checkTokenAllowance({
              userAddress: userAddr,
              chainId,
              marginToken,
              spenderAddress,
              sdk,
            }),
          );
        }
      }

      return true;
    } catch (e) {
      console.error('approve token e', e);
      return false;
    }
  },
);

export const checkTokenAllowance = createAsyncThunk(
  'user/checkTokenAllowance',
  async ({
    marginToken,
    chainId,
    userAddress: userAddr,
    spenderAddress,
    sdk,
  }: {
    marginToken: TokenInfo;
    chainId: number;
    userAddress: string;
    spenderAddress?: string;
    block?: number;
    sdk: Context;
  }): Promise<ITokenAllowanceInfo | undefined> => {
    try {
      if (marginToken?.address && isNativeTokenAddr(marginToken.address)) {
        return {
          block: 0,
          allowance: ZERO,
        };
      }
      if (!marginToken?.address) {
        return;
      }

      if (!spenderAddress) {
        spenderAddress = sdk.perp.contracts.gate.address;
      }
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.BALANCE.TOKEN_BALANCE(chainId, userAddr, spenderAddress) });

      return;
    } catch (e) {
      console.error('checkTokenAllowance token e', e);
      return {
        block: 0,
        allowance: ZERO,
      };
    }
  },
);
